import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, Timestamp, updateDoc, getDoc, setDoc, query, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import { BookingType, PrivateBlock, SettingsData } from '../../types/types';
import Pagination from '../../components/Pagination';

interface Racer {
  id: string;
  fullName: string;
  email: string;
  motherName: string;
  address: string;
  birthPlace: string;
  birthDate: string;
  idCardNumber: string;
  socialSecurityNumber: string;
  racerId: string;
  registrationName: string;
  isProfileComplete: boolean;
  updatedAt: Timestamp;
  phoneNumber: string; // Új mező: telefonszám
}

interface Booking {
  birthPlace: string | undefined;
  id: string;
  userName: string;
  userEmail: string;
  itemNumber?: number;
  date: Timestamp;
  startTime: string;
  endTime: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  bookingType: BookingType; // Módosítva: játszóház típus hozzáadva
  userId?: string;
  racerId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  updatedBy?: string;
  cancelledAt?: Timestamp;
  cancelledBy?: string;
  motherName?: string;
  birthDate?: string;
  address?: string;
  phoneNumber?: string;
  emergencyContact?: string;
  idNumber?: string;
  // Vendég adatok a racers kollekcióból
  racerDetails?: Racer;
}

// Csoportosított foglalás típus
interface GroupedBooking {
  date: Timestamp;
  startTime: string;
  endTime: string;
  bookings: Booking[];
}

// Magyar státusz fordítások
const statusTranslations = {
  pending: 'Függőben',
  confirmed: 'Megerősítve',
  completed: 'Teljesítve',
  cancelled: 'Lemondva'
};

// Add interface for consolidated day view
interface ConsolidatedEvent {
  id: string;
  type: 'booking' | 'private';
  date: Timestamp | Date;
  startTime: string;
  endTime: string;
  title: string;
  description?: string;
  serviceType: BookingType;
  status?: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  // For bookings
  userName?: string;
  userEmail?: string;
  itemNumber?: number;
  // For private events
  notes?: string;
  // For grouped bookings
  groupInfo?: GroupedBooking;
}

const Bookings: React.FC = () => {
  // Extended interface for mixed grouping (moved up for accessibility)
  interface ExtendedGroupedBooking extends GroupedBooking {
    privateEvents?: PrivateBlock[];
    hasPrivateEvents?: boolean;
  }

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [groupedBookings, setGroupedBookings] = useState<ExtendedGroupedBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<GroupedBooking | null>(null);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [filterDate, setFilterDate] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<string>('all');
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [newBooking, setNewBooking] = useState({
    userName: '',
    userEmail: '',
    kartNumber: 1,
    date: '',
    startTime: '',
    endTime: '',
  });
  const [showNewBookingUserBrowser, setShowNewBookingUserBrowser] = useState(false);
  const [newBookingRacerSearchTerm, setNewBookingRacerSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Módosítjuk, hogy állítható legyen
  const [cachedRacers, setCachedRacers] = useState<{ [key: string]: Racer }>({});
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [privateBlocks, setPrivateBlocks] = useState<PrivateBlock[]>([]);
  const [activeTab, setActiveTab] = useState<'list' | 'daily'>('list');
  const [consolidatedDate, setConsolidatedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [consolidatedEvents, setConsolidatedEvents] = useState<ConsolidatedEvent[]>([]);
  const [deletingPrivateEvent, setDeletingPrivateEvent] = useState<string | null>(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    userName: '',
    userEmail: '',
    motherName: '',
    birthDate: '',
    address: '',
    phoneNumber: '',
    idNumber: '',
  });
  const [showUserBrowser, setShowUserBrowser] = useState(false);
  const [availableRacers, setAvailableRacers] = useState<Racer[]>([]);
  const [racerSearchTerm, setRacerSearchTerm] = useState('');
  const [showNewRacerForm, setShowNewRacerForm] = useState(false);
  const [newRacerData, setNewRacerData] = useState({
    fullName: '',
    email: '',
    motherName: '',
    birthDate: '',
    address: '',
    phoneNumber: '',
    idCardNumber: '',
  });
  const [savingNewRacer, setSavingNewRacer] = useState(false);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [addingGuestToGroup, setAddingGuestToGroup] = useState<GroupedBooking | null>(null);
  const [addingGuest, setAddingGuest] = useState(false);

  const { currentUser, isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  // Elérhető gokartok (8 a követelmények szerint)
  const availableKarts = Array.from({ length: 8 }, (_, i) => i + 1);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    fetchBookings();
    fetchPrivateBlocks();
  }, [currentUser, navigate]);

  // Add new useEffect to handle filtering
  useEffect(() => {
    // Regroup bookings whenever the filter changes
    const grouped = groupBookingsByDateTime(bookings);
    setGroupedBookings(grouped);
  }, [filterDate, filterType, filterStatus, filterYear, filterMonth, bookings]);

  // Add useEffect to automatically update selectedGroup when groupedBookings changes
  useEffect(() => {
    // Ha van kiválasztott csoport és frissültek a csoportosított foglalások
    if (selectedGroup && groupedBookings.length > 0) {
      const updatedGroup = groupedBookings.find(group => 
        group.date.toMillis() === selectedGroup.date.toMillis() &&
        group.startTime === selectedGroup.startTime &&
        group.endTime === selectedGroup.endTime
      );
      
      if (updatedGroup) {
        setSelectedGroup(updatedGroup);
      }
    }
  }, [groupedBookings, selectedGroup]);

  // Add function to fetch private blocks
  const fetchAvailableRacers = async () => {
    try {
      const racersCollection = collection(db, 'racers');
      const racersSnapshot = await getDocs(racersCollection);
      
      const racersData: Racer[] = racersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Racer[];

      // Rendezés név szerint
      racersData.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'hu'));
      
      setAvailableRacers(racersData);
    } catch (error) {
      console.error('Hiba a vendégek lekérdezése során:', error);
    }
  };

  const fetchPrivateBlocks = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'bookings'));
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data() as Partial<SettingsData>;
        setPrivateBlocks(settingsData.privateBlocks || []);
      }
    } catch (error) {
      console.error('Hiba a zártkörű események lekérése során:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const bookingsRef = collection(db, 'bookings');
      let bookingsList: Booking[] = [];

      // Lekérjük az összes foglalást
      const q = query(
        bookingsRef,
        orderBy('date', 'desc')
      );

      const bookingsSnapshot = await getDocs(q);
      bookingsList = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Booking[];

      // Összegyűjtjük az egyedi racerId-kat
      const racerIds = Array.from(new Set(bookingsList
        .filter(booking => booking.racerId)
        .map(booking => booking.racerId as string)));

      // Feldaraboljuk a racerIds tömböt 30-as csoportokra
      const racerIdChunks = [];
      for (let i = 0; i < racerIds.length; i += 30) {
        racerIdChunks.push(racerIds.slice(i, i + 30));
      }

      // Lekérjük a racer adatokat chunk-onként
      const newRacers = { ...cachedRacers };
      for (const chunk of racerIdChunks) {
        if (chunk.length > 0) {
          const racersCollection = collection(db, 'racers');
          const racersQuery = query(
            racersCollection,
            where('racerId', 'in', chunk)
          );
          const racerSnapshot = await getDocs(racersQuery);

          racerSnapshot.docs.forEach(doc => {
            const racerData = doc.data() as Racer;
            newRacers[racerData.racerId] = { ...racerData, id: doc.id };
          });
        }
      }

      setCachedRacers(newRacers);

      // Foglalások kiegészítése a racer adatokkal
      bookingsList = bookingsList.map(booking => {
        if (booking.racerId && newRacers[booking.racerId]) {
          const racerData = newRacers[booking.racerId];
          return {
            ...booking,
            racerDetails: racerData,
            motherName: booking.motherName || racerData.motherName,
            address: booking.address || racerData.address,
            idNumber: booking.idNumber || racerData.idCardNumber,
            phoneNumber: booking.phoneNumber || racerData.phoneNumber,
          };
        }
        return booking;
      });

      setBookings(bookingsList);
    } catch (error) {
      console.error('Hiba a foglalások lekérdezése során:', error);
    } finally {
      setLoading(false);
    }
  };

  // Foglalások csoportosítása dátum és időpont szerint (including private events)
  const groupBookingsByDateTime = (bookings: Booking[]): ExtendedGroupedBooking[] => {
    const groups: { [key: string]: ExtendedGroupedBooking } = {};

    // First, add all bookings
    bookings.forEach(booking => {
      // Dátum szűrő
      if (filterDate) {
        try {
          let bookingDate: string;
          // Ha Firestore Timestamp objektum
          if (booking.date && typeof booking.date.toDate === 'function') {
            bookingDate = booking.date.toDate().toISOString().split('T')[0];
          } 
          // Ha már Date objektum
          else if (booking.date instanceof Date) {
            bookingDate = booking.date.toISOString().split('T')[0];
          }
          // Ha string formátum
          else if (typeof booking.date === 'string') {
            bookingDate = new Date(booking.date).toISOString().split('T')[0];
          }
          // Fallback
          else {
            console.warn('Ismeretlen dátum formátum a szűrőnél:', booking.date);
            return; // Skip this booking
          }
          
          if (bookingDate !== filterDate) return;
        } catch (error) {
          console.error('Hiba a dátum szűrés során:', error, booking.date);
          return; // Skip this booking
        }
      }

      // Típus szűrő
      if (filterType !== 'all' && booking.bookingType !== filterType) {
        return;
      }

      // Állapot szűrő
      if (filterStatus !== 'all') {
        if (filterStatus === 'mixed') {
          // Ha "vegyes" szűrő van kiválasztva, akkor csak azokat a csoportokat tartjuk meg,
          // ahol tényleg különböző státuszok vannak
          let keyDateStr: string;
          try {
            // Ha Firestore Timestamp objektum
            if (booking.date && typeof booking.date.toDate === 'function') {
              keyDateStr = booking.date.toDate().toISOString().split('T')[0];
            } 
            // Ha már Date objektum
            else if (booking.date instanceof Date) {
              keyDateStr = booking.date.toISOString().split('T')[0];
            }
            // Ha string formátum
            else if (typeof booking.date === 'string') {
              keyDateStr = new Date(booking.date).toISOString().split('T')[0];
            }
            // Fallback
            else {
              console.warn('Ismeretlen dátum formátum a vegyes szűrőnél:', booking.date);
              return; // Skip this booking
            }
          } catch (error) {
            console.error('Hiba a dátum feldolgozása során a vegyes szűrőnél:', error, booking.date);
            return; // Skip this booking
          }
          
          const key = `${keyDateStr}_${booking.startTime}_${booking.endTime}`;
          const existingGroup = groups[key];
          if (existingGroup) {
            const hasMultipleStatuses = existingGroup.bookings.some(b => b.status !== booking.status);
            if (!hasMultipleStatuses && existingGroup.bookings[0].status === booking.status) {
              return;
            }
          }
        } else if (booking.status !== filterStatus && filterStatus !== 'private') {
          return;
        }
      }

      // Kulcs létrehozása a dátum és időpont alapján
      let dateStr: string;
      try {
        // Ha Firestore Timestamp objektum
        if (booking.date && typeof booking.date.toDate === 'function') {
          dateStr = booking.date.toDate().toISOString().split('T')[0];
        } 
        // Ha már Date objektum
        else if (booking.date instanceof Date) {
          if (isNaN(booking.date.getTime())) {
            console.warn('Érvénytelen Date objektum:', booking.date);
            return; // Skip this booking
          }
          dateStr = booking.date.toISOString().split('T')[0];
        }
        // Ha string formátum
        else if (typeof booking.date === 'string') {
          const tempDate = new Date(booking.date);
          if (isNaN(tempDate.getTime())) {
            console.warn('Érvénytelen dátum string:', booking.date);
            return; // Skip this booking
          }
          dateStr = tempDate.toISOString().split('T')[0];
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum:', booking.date);
          return; // Skip this booking
        }
      } catch (error) {
        console.error('Hiba a dátum feldolgozása során:', error, booking.date);
        return; // Skip this booking
      }
      
      const key = `${dateStr}_${booking.startTime}_${booking.endTime}`;

      if (!groups[key]) {
        // Biztosítjuk, hogy a date mező Timestamp legyen
        let groupDate: Timestamp;
        if (booking.date && typeof booking.date.toDate === 'function') {
          groupDate = booking.date;
        } else if (booking.date instanceof Date) {
          if (isNaN(booking.date.getTime())) {
            console.warn('Érvénytelen Date objektum a csoport létrehozásánál:', booking.date);
            groupDate = Timestamp.now(); // Fallback
          } else {
            groupDate = Timestamp.fromDate(booking.date);
          }
        } else if (typeof booking.date === 'string') {
          const tempDate = new Date(booking.date);
          if (isNaN(tempDate.getTime())) {
            console.warn('Érvénytelen dátum string a csoport létrehozásánál:', booking.date);
            groupDate = Timestamp.now(); // Fallback
          } else {
            groupDate = Timestamp.fromDate(tempDate);
          }
        } else {
          groupDate = Timestamp.now(); // Fallback
        }

        groups[key] = {
          date: groupDate,
          startTime: booking.startTime,
          endTime: booking.endTime,
          bookings: [],
          privateEvents: [],
          hasPrivateEvents: false
        };
      }

      groups[key].bookings.push(booking);
    });

    // Then, add private events as separate groups (only if we're not filtering by booking status or specifically showing private events)
    if (filterStatus === 'all' || filterStatus === 'private') {
      privateBlocks.forEach((block, index) => {
        // Apply date filter to private events too
        if (filterDate) {
          try {
            const blockDate = new Date(block.date);
            if (isNaN(blockDate.getTime())) return;
            const blockDateStr = blockDate.toISOString().split('T')[0];
            if (blockDateStr !== filterDate) return;
          } catch (error) {
            return;
          }
        }

        // Apply type filter to private events
        if (filterType !== 'all' && block.serviceType !== filterType) {
          return;
        }

        // Skip if we're filtering by booking status but not showing private events
        if (filterStatus !== 'all' && filterStatus !== 'private') {
          return;
        }

        const startTime = `${block.startHour.toString().padStart(2, '0')}:00`;
        const endTime = `${block.endHour.toString().padStart(2, '0')}:00`;
        const blockDate = new Date(block.date);
        
        // Ellenőrizzük, hogy érvényes-e a dátum
        if (isNaN(blockDate.getTime())) {
          console.warn('Érvénytelen private event dátum:', block.date);
          return; // Skip this private event
        }
        
        const blockDateStr = blockDate.toISOString().split('T')[0];
        const key = `${blockDateStr}_${startTime}_${endTime}_private_${index}`;

        groups[key] = {
          date: Timestamp.fromDate(blockDate),
          startTime,
          endTime,
          bookings: [],
          privateEvents: [block],
          hasPrivateEvents: true
        };
      });
         }

    // Ha "vegyes" szűrő van kiválasztva, akkor csak azokat a csoportokat tartjuk meg,
    // ahol tényleg különböző státuszok vannak
    if (filterStatus === 'mixed') {
      Object.keys(groups).forEach(key => {
        const group = groups[key];
        const hasMultipleStatuses = group.bookings.some(b =>
          b.status !== group.bookings[0].status
        );
        if (!hasMultipleStatuses) {
          delete groups[key];
        }
      });
    }

    // Rendezzük a csoportokat dátum és időpont szerint
    return Object.values(groups).sort((a, b) => {
      // Először dátum szerint rendezünk
      const dateComparison = a.date.toMillis() - b.date.toMillis();
      if (dateComparison !== 0) {
        return dateComparison;
      }

      // Ha a dátum megegyezik, akkor időpont szerint rendezünk
      const aTime = a.startTime.split(':').map(Number);
      const bTime = b.startTime.split(':').map(Number);

      // Óra összehasonlítása
      if (aTime[0] !== bTime[0]) {
        return aTime[0] - bTime[0];
      }

      // Perc összehasonlítása
      return aTime[1] - bTime[1];
    });
  };

  // Dátum szűrő kezelése
  const handleFilterDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterDate(e.target.value);
  };

  // Szűrő törlése
  const clearFilters = () => {
    setFilterDate(null);
    setFilterType('all');
    setFilterStatus('all');
    setFilterYear('all');
    setFilterMonth('all');
  };

  // Típus szűrő kezelése
  const handleFilterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterType(e.target.value);
  };

  // Állapot szűrő kezelése
  const handleFilterStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterStatus(e.target.value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewBooking(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddBooking = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const bookingData = {
        userName: newBooking.userName,
        userEmail: newBooking.userEmail,
        kartNumber: parseInt(newBooking.kartNumber.toString()),
        date: Timestamp.fromDate(new Date(newBooking.date)),
        startTime: newBooking.startTime,
        endTime: newBooking.endTime,
        status: 'pending' as const,
        createdAt: Timestamp.now(),
        createdBy: currentUser?.uid
      };

      await addDoc(collection(db, 'bookings'), bookingData);

      // Űrlap visszaállítása és foglalások frissítése
      setNewBooking({
        userName: '',
        userEmail: '',
        kartNumber: 1,
        date: '',
        startTime: '',
        endTime: '',
      });
      setShowNewBookingUserBrowser(false);
      setNewBookingRacerSearchTerm('');
      setShowAddForm(false);
      fetchBookings();
    } catch (error) {
      console.error('Hiba a foglalás hozzáadása során:', error);
    }
  };

  const handleDeleteBooking = async (id: string, bookingType: BookingType, userName?: string, deviceNumber?: number, date?: Timestamp, startTime?: string) => {
    let confirmMessage = 'Biztosan törölni szeretnéd ezt a foglalást?';

    if (userName && deviceNumber && date && startTime) {
      const deviceType = bookingType === 'gokart' ? 'Gokart' :
        bookingType === 'simulator' ? 'Szimulátor' :
          bookingType === 'vr' ? 'VR' : 'Játszóház';
      const formattedDate = formatDate(date);
      confirmMessage = `Biztosan törölni szeretnéd ${userName} foglalását (${deviceType} #${deviceNumber}, ${formattedDate}, ${startTime})?`;
    }

    if (window.confirm(confirmMessage)) {
      try {
        // Most már csak a bookings kollekcióból törlünk
        await deleteDoc(doc(db, 'bookings', id));

        // Frissítjük a foglalások listáját
        const updatedBookings = bookings.filter(booking => booking.id !== id);
        setBookings(updatedBookings);

        // Frissítjük a csoportosított foglalásokat is
        const updatedGrouped = groupBookingsByDateTime(updatedBookings);
        setGroupedBookings(updatedGrouped);

        // Ha a részletek modal nyitva van, frissítjük a kiválasztott csoportot is
        if (selectedGroup) {
          const updatedGroup = {
            ...selectedGroup,
            bookings: selectedGroup.bookings.filter(booking => booking.id !== id)
          };

          // Ha a csoport üres lett, bezárjuk a modalt
          if (updatedGroup.bookings.length === 0) {
            closeGroupDetails();
          } else {
            setSelectedGroup(updatedGroup);
          }
        }

        // Sikeres törlés üzenet
        setStatusUpdateSuccess(true);
        setTimeout(() => {
          setStatusUpdateSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Hiba a foglalás törlése során:', error);
        setStatusUpdateError('Nem sikerült törölni a foglalást. Kérjük, próbáld újra!');
        setTimeout(() => {
          setStatusUpdateError(null);
        }, 3000);
      }
    }
  };

  const formatDate = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleDateString('hu-HU');
  };

  // Foglalás státuszának frissítése
  const updateBookingStatus = async (id: string, bookingType: BookingType, newStatus: 'confirmed' | 'cancelled' | 'completed') => {
    try {
      const bookingRef = doc(db, 'bookings', id);
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: Timestamp.now(),
        updatedBy: currentUser?.uid
      });

      // Frissítjük a foglalások listáját
      const updatedBookings = bookings.map(booking =>
        booking.id === id
          ? { ...booking, status: newStatus }
          : booking
      );

      setBookings(updatedBookings);

      // Frissítjük a csoportosított foglalásokat
      const updatedGrouped = groupBookingsByDateTime(updatedBookings);
      setGroupedBookings(updatedGrouped);

      // Frissítjük a kiválasztott csoportot is, ha van ilyen
      if (selectedGroup) {
        const updatedSelectedGroup = {
          ...selectedGroup,
          bookings: selectedGroup.bookings.map(booking =>
            booking.id === id
              ? { ...booking, status: newStatus }
              : booking
          )
        };
        setSelectedGroup(updatedSelectedGroup);
      }

      setStatusUpdateSuccess(true);
      setStatusUpdateError(null);

      setTimeout(() => {
        setStatusUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a foglalás státuszának frissítése során:', error);
      setStatusUpdateError('Nem sikerült frissíteni a foglalás státuszát. Kérjük, próbáld újra!');

      setTimeout(() => {
        setStatusUpdateError(null);
      }, 3000);
    }
  };

  // Módosítjuk a updateGroupStatus függvényt
  const updateGroupStatus = async (groupedBooking: GroupedBooking, newStatus: 'confirmed' | 'cancelled' | 'completed') => {
    try {
      // Csak azokat a foglalásokat módosítjuk, amelyeknél ez lehetséges
      const updateableBookings = groupedBooking.bookings.filter(booking => {
        if (newStatus === 'cancelled') {
          // Csak a pending státuszú foglalásokat lehet lemondani
          return booking.status === 'pending';
        } else if (newStatus === 'confirmed') {
          // Csak a pending státuszú foglalásokat lehet megerősíteni
          return booking.status === 'pending';
        } else if (newStatus === 'completed') {
          // Csak a confirmed státuszú foglalásokat lehet teljesíteni
          return booking.status === 'confirmed';
        }
        return false;
      });

      if (updateableBookings.length === 0) {
        setStatusUpdateError('Nincs olyan foglalás, amit módosítani lehetne.');
        setTimeout(() => {
          setStatusUpdateError(null);
        }, 3000);
        return;
      }

      const updatePromises = updateableBookings.map(booking => {
        return updateDoc(doc(db, 'bookings', booking.id), {
          status: newStatus,
          updatedAt: Timestamp.now(),
          updatedBy: currentUser?.uid
        });
      });

      await Promise.all(updatePromises);

      // Frissítjük a foglalások listáját
      const updatedBookings = bookings.map(booking =>
        updateableBookings.some(b => b.id === booking.id)
          ? { ...booking, status: newStatus }
          : booking
      );

      setBookings(updatedBookings);

      // Frissítjük a csoportosított foglalásokat
      const updatedGrouped = groupBookingsByDateTime(updatedBookings);
      setGroupedBookings(updatedGrouped);

      // Frissítjük a kiválasztott csoportot is, ha ez az aktuális csoport
      if (selectedGroup && selectedGroup.bookings.some(b => groupedBooking.bookings.some(gb => gb.id === b.id))) {
        const updatedSelectedGroup = {
          ...selectedGroup,
          bookings: selectedGroup.bookings.map(booking =>
            groupedBooking.bookings.some(b => b.id === booking.id)
              ? { ...booking, status: newStatus }
              : booking
          )
        };
        setSelectedGroup(updatedSelectedGroup);
      }

      setStatusUpdateSuccess(true);
      setStatusUpdateError(null);

      setTimeout(() => {
        setStatusUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Hiba a foglalás csoport státuszának frissítése során:', error);
      setStatusUpdateError('Nem sikerült frissíteni a foglalások státuszát. Kérjük, próbáld újra!');

      setTimeout(() => {
        setStatusUpdateError(null);
      }, 3000);
    }
  };

  // Mobil menü megnyitása/bezárása
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Kilépés kezelése
  const handleLogout = async () => {
    if (window.confirm('Biztosan ki szeretnél jelentkezni?')) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Hiba a kijelentkezés során:', error);
      }
    }
  };

  // Foglalás csoport részleteinek megjelenítése
  const viewGroupDetails = (group: GroupedBooking) => {
    setSelectedGroup(group);
    setShowGroupDetails(true);
  };

  // Foglalás csoport részleteinek bezárása
  const closeGroupDetails = () => {
    setShowGroupDetails(false);
    setSelectedGroup(null);
  };

  // Foglalás csoport törlése
  const handleDeleteGroup = async (group: GroupedBooking) => {
    const formattedDate = formatDate(group.date);
    const confirmMessage = `Biztosan törölni szeretnéd az összes foglalást (${formattedDate}, ${group.startTime} - ${group.endTime}, ${group.bookings.length} eszköz)?`;

    if (window.confirm(confirmMessage)) {
      try {
        // Most már csak a bookings kollekcióból törlünk
        const deletePromises = group.bookings.map(booking =>
          deleteDoc(doc(db, 'bookings', booking.id))
        );

        await Promise.all(deletePromises);

        // Frissítjük a foglalások listáját
        const updatedBookings = bookings.filter(booking =>
          !group.bookings.some(b => b.id === booking.id)
        );

        setBookings(updatedBookings);

        // Frissítjük a csoportosított foglalásokat is
        const updatedGrouped = groupBookingsByDateTime(updatedBookings);
        setGroupedBookings(updatedGrouped);

        // Ha a részletek modal nyitva van, bezárjuk
        if (selectedGroup && selectedGroup.bookings.some(b => group.bookings.some(gb => gb.id === b.id))) {
          closeGroupDetails();
        }

        // Sikeres törlés üzenet
        setStatusUpdateSuccess(true);
        setTimeout(() => {
          setStatusUpdateSuccess(false);
        }, 3000);
      } catch (error) {
        console.error('Hiba a foglalás csoport törlése során:', error);
        setStatusUpdateError('Nem sikerült törölni a foglalásokat. Kérjük, próbáld újra!');
        setTimeout(() => {
          setStatusUpdateError(null);
        }, 3000);
      }
    }
  };

  // Add this function before the return statement
  const handleSortByDate = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Update the paginatedBookings sort function
  const filteredBookings = groupedBookings.filter(group => {
    // Cast to ExtendedGroupedBooking to access private event properties
    const extendedGroup = group as ExtendedGroupedBooking;
    
    // Dátum szűrő
    if (filterDate) {
      try {
        const filterDateStr = filterDate.toString().split('T')[0];
        let groupDate: Date;
        
        // Ha Firestore Timestamp objektum
        if (group.date && typeof group.date.toDate === 'function') {
          groupDate = group.date.toDate();
        } 
        // Ha már Date objektum
        else if (group.date instanceof Date) {
          groupDate = group.date;
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum a szűrőnél:', group.date);
          return false;
        }
        
        const groupDateStr = `${groupDate.getFullYear()}-${String(groupDate.getMonth() + 1).padStart(2, '0')}-${String(groupDate.getDate()).padStart(2, '0')}`;
        if (filterDateStr !== groupDateStr) return false;
      } catch (error) {
        console.error('Hiba a dátum szűrés során:', error, group.date);
        return false;
      }
    }

    // Év szűrő
    if (filterYear !== 'all') {
      try {
        let groupYear: string;
        
        // Ha Firestore Timestamp objektum
        if (group.date && typeof group.date.toDate === 'function') {
          groupYear = group.date.toDate().getFullYear().toString();
        } 
        // Ha már Date objektum
        else if (group.date instanceof Date) {
          groupYear = group.date.getFullYear().toString();
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum az év szűrőnél:', group.date);
          return false;
        }
        
        if (groupYear !== filterYear) return false;
      } catch (error) {
        console.error('Hiba az év szűrés során:', error, group.date);
        return false;
      }
    }

    // Hónap szűrő
    if (filterMonth !== 'all') {
      try {
        let groupMonth: string;
        
        // Ha Firestore Timestamp objektum
        if (group.date && typeof group.date.toDate === 'function') {
          groupMonth = String(group.date.toDate().getMonth() + 1).padStart(2, '0');
        } 
        // Ha már Date objektum
        else if (group.date instanceof Date) {
          groupMonth = String(group.date.getMonth() + 1).padStart(2, '0');
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum a hónap szűrőnél:', group.date);
          return false;
        }
        
        if (groupMonth !== filterMonth) return false;
      } catch (error) {
        console.error('Hiba a hónap szűrés során:', error, group.date);
        return false;
      }
    }

    // Speciális kezelés a "csak zártkörű események" szűrőhöz
    if (filterStatus === 'private') {
      // Csak azokat a csoportokat tartjuk meg, amelyek zártkörű eseményeket tartalmaznak
      return extendedGroup.hasPrivateEvents === true;
    }

    // Típus szűrő - zártkörű eseményeknél is ellenőrizzük a típust
    if (filterType !== 'all') {
      let hasMatchingType = false;
      
      // Ellenőrizzük a foglalásokat
      if (group.bookings.some(b => b.bookingType === filterType)) {
        hasMatchingType = true;
      }
      
      // Ellenőrizzük a zártkörű eseményeket is
      if (extendedGroup.privateEvents?.some(pe => pe.serviceType === filterType)) {
        hasMatchingType = true;
      }
      
      if (!hasMatchingType) return false;
    }

    // Állapot szűrő - kihagyjuk a zártkörű eseményeket, mert azoknak nincs státusza
    if (filterStatus !== 'all' && filterStatus !== 'private') {
      // Vegyes státusz kezelése
      if (filterStatus === 'mixed') {
        const statuses = group.bookings.map(b => b.status);
        const uniqueStatuses = Array.from(new Set(statuses));
        if (uniqueStatuses.length <= 1) return false;
      } else {
        if (!group.bookings.some(b => b.status === filterStatus)) return false;
      }
    }

    return true;
  });

  const paginatedBookings = filteredBookings
    .sort((a: GroupedBooking, b: GroupedBooking) => {
      // Először dátum szerint rendezünk
      const dateComparison = sortOrder === 'asc'
        ? a.date.toMillis() - b.date.toMillis()
        : b.date.toMillis() - a.date.toMillis();

      if (dateComparison !== 0) {
        return dateComparison;
      }

      // Ha a dátum megegyezik, akkor időpont szerint rendezünk
      const aTime = a.startTime.split(':').map(Number);
      const bTime = b.startTime.split(':').map(Number);

      // Óra összehasonlítása
      if (aTime[0] !== bTime[0]) {
        return sortOrder === 'asc' ? aTime[0] - bTime[0] : bTime[0] - aTime[0];
      }

      // Perc összehasonlítása
      return sortOrder === 'asc' ? aTime[1] - bTime[1] : bTime[1] - aTime[1];
    })
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Add function to get available years from bookings
  const getAvailableYears = () => {
    const years = new Set<string>();
    bookings.forEach(booking => {
      try {
        let year: number;
        // Ha Firestore Timestamp objektum
        if (booking.date && typeof booking.date.toDate === 'function') {
          year = booking.date.toDate().getFullYear();
        } 
        // Ha már Date objektum
        else if (booking.date instanceof Date) {
          year = booking.date.getFullYear();
        }
        // Ha string formátum
        else if (typeof booking.date === 'string') {
          year = new Date(booking.date).getFullYear();
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum a getAvailableYears-ben:', booking.date);
          return; // Skip this booking
        }
        
        years.add(year.toString());
      } catch (error) {
        console.error('Hiba az év kinyerése során:', error, booking.date);
      }
    });
    return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
  };

  // Add function to generate consolidated events for a specific date
  const generateConsolidatedEvents = (date: string) => {
    const events: ConsolidatedEvent[] = [];
    
    // Get grouped bookings for the selected date
    const groupsForDate = groupedBookings.filter(group => {
      try {
        let groupDate: string;
        
        // Ha Firestore Timestamp objektum
        if (group.date && typeof group.date.toDate === 'function') {
          groupDate = group.date.toDate().toISOString().split('T')[0];
        } 
        // Ha már Date objektum
        else if (group.date instanceof Date) {
          if (isNaN(group.date.getTime())) {
            console.warn('Érvénytelen Date objektum a generateConsolidatedEvents-ben:', group.date);
            return false;
          }
          groupDate = group.date.toISOString().split('T')[0];
        }
        // Fallback
        else {
          console.warn('Ismeretlen dátum formátum a generateConsolidatedEvents-ben:', group.date);
          return false;
        }
        
        return groupDate === date;
      } catch (error) {
        console.error('Hiba a dátum feldolgozása során a generateConsolidatedEvents-ben:', error, group.date);
        return false;
      }
    });

    // Convert grouped bookings to consolidated events
    groupsForDate.forEach(group => {
      const extendedGroup = group as ExtendedGroupedBooking;
      
      if (extendedGroup.hasPrivateEvents && extendedGroup.bookings.length === 0) {
        // This is a private event only group
        extendedGroup.privateEvents?.forEach((block, index) => {
          events.push({
            id: `private-${index}-${block.date}-${group.startTime}`,
            type: 'private',
            date: group.date,
            startTime: group.startTime,
            endTime: group.endTime,
            title: `Zártkörű esemény - ${getServiceTypeLabel(block.serviceType)}`,
            description: block.description || 'Nincs leírás',
            serviceType: block.serviceType,
            notes: block.notes
          });
        });
      } else {
        // This is a booking group (might also have private events)
        const bookingTypes = Array.from(new Set(group.bookings.map(b => b.bookingType)));
        const statuses = Array.from(new Set(group.bookings.map(b => b.status)));
        
        // Create a single event for the entire group
        const groupTitle = bookingTypes.length === 1 
          ? `${group.bookings.length}x ${getServiceTypeLabel(bookingTypes[0])} foglalás`
          : `${group.bookings.length}x Vegyes foglalás`;
          
        const groupDescription = group.bookings.length === 1 
          ? `${group.bookings[0].userName} - ${group.bookings[0].userEmail}${group.bookings[0].itemNumber ? ` - Eszköz: ${group.bookings[0].itemNumber}` : ''}`
          : `${group.bookings.map(b => b.userName).join(', ')}`;
        
        // Determine group status
        let groupStatus: 'pending' | 'confirmed' | 'completed' | 'cancelled' | undefined;
        if (statuses.length === 1) {
          groupStatus = statuses[0];
        } else {
          // Mixed statuses - use the most relevant one
          if (statuses.includes('pending')) groupStatus = 'pending';
          else if (statuses.includes('confirmed')) groupStatus = 'confirmed';
          else if (statuses.includes('completed')) groupStatus = 'completed';
          else groupStatus = 'cancelled';
        }

        events.push({
          id: `group-${group.date.toMillis()}-${group.startTime}-${group.endTime}`,
          type: 'booking',
          date: group.date,
          startTime: group.startTime,
          endTime: group.endTime,
          title: groupTitle,
          description: groupDescription,
          serviceType: bookingTypes[0], // Use first type for filtering
          status: groupStatus,
          // Store group info for actions
          groupInfo: group
        });
      }
    });

    // Sort events by start time
    events.sort((a, b) => {
      const timeA = a.startTime.replace(':', '');
      const timeB = b.startTime.replace(':', '');
      return parseInt(timeA) - parseInt(timeB);
    });

    setConsolidatedEvents(events);
  };

  // Helper function to get service type labels
  const getServiceTypeLabel = (type: BookingType): string => {
    switch (type) {
      case 'gokart': return 'Gokart';
      case 'vr': return 'VR Élmény';
      case 'simulator': return 'Szimulátor';
      case 'playhouse': return 'Játszóház';
      default: return type;
    }
  };

  // Update consolidated view when date changes or bookings change
  useEffect(() => {
    if (activeTab === 'daily') {
      generateConsolidatedEvents(consolidatedDate);
    }
  }, [consolidatedDate, bookings, privateBlocks, activeTab, groupedBookings]);

  // Refresh consolidated events after status updates
  useEffect(() => {
    if (activeTab === 'daily' && (statusUpdateSuccess || statusUpdateError)) {
      generateConsolidatedEvents(consolidatedDate);
    }
  }, [statusUpdateSuccess, statusUpdateError, activeTab, consolidatedDate]);

  // Ensure consolidated events are generated when switching to daily tab
  useEffect(() => {
    if (activeTab === 'daily' && consolidatedEvents.length === 0 && groupedBookings.length > 0) {
      generateConsolidatedEvents(consolidatedDate);
    }
  }, [activeTab, consolidatedEvents.length, groupedBookings.length, consolidatedDate]);

  // Function to delete private event
  const handlePrintDailyView = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Nem sikerült megnyitni a nyomtatási ablakot. Ellenőrizd a böngésző popup beállításait.');
      return;
    }

    const filteredEvents = consolidatedEvents.filter(event => {
      if (filterType !== 'all' && event.serviceType !== filterType) return false;
      if (filterStatus === 'private' && event.type !== 'private') return false;
      if (filterStatus !== 'all' && filterStatus !== 'private' && event.type === 'booking' && event.status !== filterStatus) return false;
      return true;
    });

    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Napi áttekintés - ${consolidatedDate}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .date { font-size: 24px; font-weight: bold; margin-bottom: 5px; }
          .summary { font-size: 16px; color: #666; margin-bottom: 20px; }
          .event { margin-bottom: 15px; padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
          .event-private { background-color: #fef3c7; border-left: 4px solid #f59e0b; }
          .event-confirmed { background-color: #dcfce7; border-left: 4px solid #22c55e; }
          .event-pending { background-color: #dbeafe; border-left: 4px solid #3b82f6; }
          .event-completed { background-color: #f3f4f6; border-left: 4px solid #6b7280; }
          .event-cancelled { background-color: #fee2e2; border-left: 4px solid #ef4444; }
          .event-time { font-weight: bold; font-size: 16px; }
          .event-title { font-size: 14px; font-weight: 600; margin: 5px 0; }
          .event-description { font-size: 12px; color: #666; }
          .event-notes { font-size: 12px; color: #666; font-style: italic; margin-top: 5px; }
          .event-badges { margin: 5px 0; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 600; margin-right: 5px; }
          .badge-private { background-color: #fbbf24; color: #92400e; }
          .badge-gokart { background-color: #22c55e; color: white; }
          .badge-simulator { background-color: #3b82f6; color: white; }
          .badge-vr { background-color: #8b5cf6; color: white; }
          .badge-playhouse { background-color: #f59e0b; color: white; }
          .badge-confirmed { background-color: #22c55e; color: white; }
          .badge-pending { background-color: #3b82f6; color: white; }
          .badge-completed { background-color: #6b7280; color: white; }
          .badge-cancelled { background-color: #ef4444; color: white; }
          .print-footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
          @media print {
            body { margin: 0; }
            .event { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
                     <div class="date">${new Date(consolidatedDate + 'T00:00:00').toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
          <div class="summary">Napi áttekintés - ${filteredEvents.length} esemény</div>
        </div>
        
        ${filteredEvents.map(event => `
          <div class="event event-${event.type === 'private' ? 'private' : event.status || 'pending'}">
            <div class="event-time">${event.startTime} - ${event.endTime}</div>
            <div class="event-badges">
              <span class="badge badge-${event.type === 'private' ? 'private' : event.serviceType}">
                ${event.type === 'private' ? 'Zártkörű' : getServiceTypeLabel(event.serviceType)}
              </span>
              ${event.type === 'booking' && event.status ? `
                <span class="badge badge-${event.status}">
                  ${statusTranslations[event.status]}
                </span>
              ` : ''}
            </div>
            <div class="event-title">${event.title}</div>
            <div class="event-description">${event.description || ''}</div>
            ${event.notes ? `<div class="event-notes">Megjegyzés: ${event.notes}</div>` : ''}
          </div>
        `).join('')}
        
        <div class="print-footer">
          Nyomtatva: ${new Date().toLocaleString('hu-HU')}
        </div>
      </body>
      </html>
    `;

    try {
      printWindow.document.write(printHTML);
      printWindow.document.close();
      printWindow.focus();
      
      // Wait a bit for the content to load before printing
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error('Print error:', error);
      printWindow.close();
      
      // Fallback: try to print current page
      alert('Popup nyomtatás nem sikerült. Próbáld meg a böngésző nyomtatás funkcióját (Ctrl+P).');
    }
  };

  const handleEditBooking = (booking: Booking) => {
    setEditingBooking(booking);
    setEditFormData({
      userName: booking.userName || '',
      userEmail: booking.userEmail || '',
      motherName: booking.motherName || booking.racerDetails?.motherName || '',
      birthDate: booking.birthDate || booking.racerDetails?.birthDate || '',
      address: booking.address || booking.racerDetails?.address || '',
      phoneNumber: booking.phoneNumber || booking.racerDetails?.phoneNumber || '',
      idNumber: booking.idNumber || booking.racerDetails?.idCardNumber || '',
    });
    setShowEditModal(true);
    setShowUserBrowser(false);
    setRacerSearchTerm('');
    
    // Racers betöltése a tallózáshoz
    if (availableRacers.length === 0) {
      fetchAvailableRacers();
    }
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingBooking) return;

    try {
      const bookingRef = doc(db, 'bookings', editingBooking.id);
      const updateData: any = {
        userName: editFormData.userName || '',
        userEmail: editFormData.userEmail || '',
        updatedAt: Timestamp.now(),
        updatedBy: currentUser?.uid || ''
      };

      // Csak akkor adjuk hozzá a mezőket, ha nem üresek
      if (editFormData.motherName) updateData.motherName = editFormData.motherName;
      if (editFormData.birthDate) updateData.birthDate = editFormData.birthDate;
      if (editFormData.address) updateData.address = editFormData.address;
      if (editFormData.phoneNumber) updateData.phoneNumber = editFormData.phoneNumber;
      if (editFormData.idNumber) updateData.idNumber = editFormData.idNumber;

      // Frissítjük a racerId-t (lehet null is, ha eltávolítottuk)
      updateData.racerId = editingBooking.racerId || null;

      await updateDoc(bookingRef, updateData);

            // Frissítjük a foglalások listáját
      const updatedBookings = bookings.map(booking =>
        booking.id === editingBooking.id
          ? {
              ...booking,
              userName: editFormData.userName,
              userEmail: editFormData.userEmail,
              motherName: editFormData.motherName,
              birthDate: editFormData.birthDate,
              address: editFormData.address,
              phoneNumber: editFormData.phoneNumber,
              idNumber: editFormData.idNumber,
              racerId: editingBooking.racerId,
              racerDetails: editingBooking.racerDetails,
            }
          : booking
      );

      setBookings(updatedBookings);

      // Frissítjük a csoportosított foglalásokat
      const updatedGrouped = groupBookingsByDateTime(updatedBookings);
      setGroupedBookings(updatedGrouped);

      // Frissítjük a kiválasztott csoportot is, ha van ilyen
      if (selectedGroup) {
        const updatedSelectedGroup = {
          ...selectedGroup,
          bookings: selectedGroup.bookings.map(booking =>
            booking.id === editingBooking.id
              ? { 
                  ...booking, 
                  userName: editFormData.userName,
                  userEmail: editFormData.userEmail,
                  motherName: editFormData.motherName,
                  birthDate: editFormData.birthDate,
                  address: editFormData.address,
                  phoneNumber: editFormData.phoneNumber,
                  idNumber: editFormData.idNumber,
                  racerId: editingBooking.racerId,
                  racerDetails: editingBooking.racerDetails,
                }
              : booking
          )
        };
        setSelectedGroup(updatedSelectedGroup);
      }

      setShowEditModal(false);
      setEditingBooking(null);
      setStatusUpdateSuccess(true);
      setTimeout(() => setStatusUpdateSuccess(false), 3000);

    } catch (error) {
      console.error('Hiba a foglalás szerkesztése során:', error);
      setStatusUpdateError('Nem sikerült menteni a változtatásokat. Kérjük, próbáld újra!');
      setTimeout(() => setStatusUpdateError(null), 3000);
    }
  };

  const handleSelectRacer = (racer: Racer) => {
    setEditFormData({
      userName: racer.fullName || '',
      userEmail: racer.email || '',
      motherName: racer.motherName || '',
      birthDate: racer.birthDate || '',
      address: racer.address || '',
      phoneNumber: racer.phoneNumber || '',
      idNumber: racer.idCardNumber || '',
    });
    
    // Hozzárendeljük a vendéget a foglaláshoz
    if (editingBooking) {
      setEditingBooking({
        ...editingBooking,
        racerId: racer.racerId,
        racerDetails: racer
      });
    }
    
    setShowUserBrowser(false);
    setRacerSearchTerm('');
  };

  const handleNewRacerFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewRacerData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveNewRacer = async () => {
    if (!newRacerData.fullName.trim() || !newRacerData.email.trim()) {
      alert('A név és email mező kitöltése kötelező!');
      return;
    }

    try {
      setSavingNewRacer(true);

      // Generálunk egy új racer ID-t
      const racerId = `R${Math.floor(100000 + Math.random() * 900000)}`;

      // Mentjük az új vendéget a racers kollekcióba
      const racersRef = collection(db, 'racers');
      const newRacerDoc: any = {
        racerId: racerId,
        fullName: newRacerData.fullName,
        email: newRacerData.email,
        isProfileComplete: !!(newRacerData.fullName && newRacerData.email && newRacerData.motherName && newRacerData.birthDate && newRacerData.address && newRacerData.phoneNumber && newRacerData.idCardNumber),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        userId: currentUser?.uid || ''
      };

      // Csak akkor adjuk hozzá a mezőket, ha nem üresek
      if (newRacerData.motherName) newRacerDoc.motherName = newRacerData.motherName;
      if (newRacerData.birthDate) newRacerDoc.birthDate = newRacerData.birthDate;
      if (newRacerData.address) newRacerDoc.address = newRacerData.address;
      if (newRacerData.phoneNumber) newRacerDoc.phoneNumber = newRacerData.phoneNumber;
      if (newRacerData.idCardNumber) newRacerDoc.idCardNumber = newRacerData.idCardNumber;

      await addDoc(racersRef, newRacerDoc);

      // Kitöltjük a form adatokat az új vendég adataival
      setEditFormData({
        userName: newRacerData.fullName,
        userEmail: newRacerData.email,
        motherName: newRacerData.motherName,
        birthDate: newRacerData.birthDate,
        address: newRacerData.address,
        phoneNumber: newRacerData.phoneNumber,
        idNumber: newRacerData.idCardNumber,
      });

      // Hozzárendeljük az új vendéget a foglaláshoz
      if (editingBooking) {
        const newRacerWithId: Racer = {
          id: racerId,
          racerId: racerId,
          fullName: newRacerData.fullName,
          email: newRacerData.email,
          motherName: newRacerData.motherName,
          birthDate: newRacerData.birthDate,
          address: newRacerData.address,
          phoneNumber: newRacerData.phoneNumber,
          idCardNumber: newRacerData.idCardNumber,
          birthPlace: '', // Alapértelmezett érték
          socialSecurityNumber: '', // Alapértelmezett érték
          registrationName: newRacerData.fullName, // Használjuk a nevet
          isProfileComplete: !!(newRacerData.fullName && newRacerData.email && newRacerData.motherName && newRacerData.birthDate && newRacerData.address && newRacerData.phoneNumber && newRacerData.idCardNumber),
          updatedAt: Timestamp.now()
        };
        
        setEditingBooking({
          ...editingBooking,
          racerId: racerId,
          racerDetails: newRacerWithId
        });
      }

      // Reseteljük az új vendég formot
      setNewRacerData({
        fullName: '',
        email: '',
        motherName: '',
        birthDate: '',
        address: '',
        phoneNumber: '',
        idCardNumber: '',
      });

      setShowNewRacerForm(false);
      setShowUserBrowser(false);

      // Frissítjük a vendégek listáját
      await fetchAvailableRacers();

      alert('Új vendég sikeresen hozzáadva!');
    } catch (error) {
      console.error('Hiba az új vendég mentése során:', error);
      alert('Hiba történt az új vendég mentése során. Kérjük, próbáld újra!');
    } finally {
      setSavingNewRacer(false);
    }
  };

  const handleCancelNewRacer = () => {
    setNewRacerData({
      fullName: '',
      email: '',
      motherName: '',
      birthDate: '',
      address: '',
      phoneNumber: '',
      idCardNumber: '',
    });
    setShowNewRacerForm(false);
  };

  const handleSelectRacerForNewBooking = (racer: Racer) => {
    setNewBooking(prev => ({
      ...prev,
      userName: racer.fullName || '',
      userEmail: racer.email || '',
    }));
    setShowNewBookingUserBrowser(false);
    setNewBookingRacerSearchTerm('');
  };

  const handleToggleNewBookingUserBrowser = async () => {
    setShowNewBookingUserBrowser(!showNewBookingUserBrowser);
    if (!showNewBookingUserBrowser && availableRacers.length === 0) {
      await fetchAvailableRacers();
    }
  };

  const handleToggleUserBrowser = async () => {
    setShowUserBrowser(!showUserBrowser);
    if (!showUserBrowser && availableRacers.length === 0) {
      await fetchAvailableRacers();
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingBooking(null);
    setShowUserBrowser(false);
    setRacerSearchTerm('');
    setShowNewRacerForm(false);
    setNewRacerData({
      fullName: '',
      email: '',
      motherName: '',
      birthDate: '',
      address: '',
      phoneNumber: '',
      idCardNumber: '',
    });
    setEditFormData({
      userName: '',
      userEmail: '',
      motherName: '',
      birthDate: '',
      address: '',
      phoneNumber: '',
      idNumber: '',
    });
  };

  const handleAddGuestToGroup = (group: GroupedBooking) => {
    setAddingGuestToGroup(group);
    setShowAddGuestModal(true);
    setAddingGuest(false); // Biztosítjuk, hogy a loading state false legyen
    if (availableRacers.length === 0) {
      fetchAvailableRacers();
    }
  };

  const handleSelectRacerForGroup = async (racer: Racer) => {
    if (!addingGuestToGroup || addingGuest) return; // Megakadályozzuk a dupla végrehajtást

    // Beállítjuk a loading state-et
    setAddingGuest(true);

    // Azonnal bezárjuk a modalt a jobb felhasználói élmény érdekében
    setShowAddGuestModal(false);
    setAddingGuestToGroup(null);

    try {
      // Keresünk egy szabad eszköz számot
      const existingNumbers = addingGuestToGroup.bookings.map(b => b.itemNumber || 1);
      let newItemNumber = 1;
      while (existingNumbers.includes(newItemNumber)) {
        newItemNumber++;
      }

      // Létrehozunk egy új foglalást ugyanazzal a dátummal és időponttal
      const newBookingData: any = {
        userName: racer.fullName || '',
        userEmail: racer.email || '',
        date: addingGuestToGroup.date,
        startTime: addingGuestToGroup.startTime,
        endTime: addingGuestToGroup.endTime,
        status: 'pending',
        bookingType: addingGuestToGroup.bookings[0].bookingType, // Használjuk az első foglalás típusát
        racerId: racer.racerId || '',
        itemNumber: newItemNumber,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        userId: currentUser?.uid || ''
      };

      // Csak akkor adjuk hozzá a mezőket, ha nem undefined vagy üres string
      if (racer.motherName) newBookingData.motherName = racer.motherName;
      if (racer.birthDate) newBookingData.birthDate = racer.birthDate;
      if (racer.birthPlace) newBookingData.birthPlace = racer.birthPlace;
      if (racer.address) newBookingData.address = racer.address;
      if (racer.phoneNumber) newBookingData.phoneNumber = racer.phoneNumber;
      if (racer.idCardNumber) newBookingData.idNumber = racer.idCardNumber;

      // Mentjük az adatbázisba
      await addDoc(collection(db, 'bookings'), newBookingData);
      
      // Frissítjük az adatokat a szerverről
      await fetchBookings();
      
      // A fetchBookings() után a useEffect automatikusan újracsoportosítja a bookings-t
      // Frissítjük a napi áttekintést is, ha az aktív
      if (activeTab === 'daily') {
        generateConsolidatedEvents(consolidatedDate);
      }

      setStatusUpdateSuccess(true);
      setTimeout(() => setStatusUpdateSuccess(false), 3000);

    } catch (error) {
      console.error('Hiba a vendég hozzáadása során:', error);
      setStatusUpdateError('Nem sikerült hozzáadni a vendéget. Kérjük, próbáld újra!');
      setTimeout(() => setStatusUpdateError(null), 3000);
    } finally {
      // Mindig visszaállítjuk a loading state-et
      setAddingGuest(false);
    }
  };

  const handleCancelAddGuest = () => {
    setShowAddGuestModal(false);
    setAddingGuestToGroup(null);
    setAddingGuest(false); // Visszaállítjuk a loading state-et
  };

  const handleDeletePrivateEvent = async (eventId: string, eventDate: string, startTime: string, endTime: string) => {
    const confirmMessage = `Biztosan törölni szeretnéd ezt a zártkörű eseményt?\n\nDátum: ${eventDate}\nIdőpont: ${startTime} - ${endTime}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeletingPrivateEvent(eventId);
      
      // Get current settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'bookings'));
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data() as Partial<SettingsData>;
        const currentPrivateBlocks = settingsData.privateBlocks || [];
        
        // Find and remove the private block
        const updatedPrivateBlocks = currentPrivateBlocks.filter(block => {
          const blockStartTime = `${block.startHour.toString().padStart(2, '0')}:00`;
          const blockEndTime = `${block.endHour.toString().padStart(2, '0')}:00`;
          
          // Validáljuk a dátumot
          const tempBlockDate = new Date(block.date);
          if (isNaN(tempBlockDate.getTime())) {
            console.warn('Érvénytelen private block dátum a törlésnél:', block.date);
            return true; // Keep the block if date is invalid
          }
          
          const blockDate = tempBlockDate.toISOString().split('T')[0];
          
          return !(blockDate === eventDate && blockStartTime === startTime && blockEndTime === endTime);
        });
        
        // Update settings with removed private block
        await setDoc(doc(db, 'settings', 'bookings'), {
          ...settingsData,
          privateBlocks: updatedPrivateBlocks
        });
        
        // Update local state
        setPrivateBlocks(updatedPrivateBlocks);
        
        // Refresh consolidated events
        generateConsolidatedEvents(consolidatedDate);
        
        // Show success message
        setStatusUpdateSuccess(true);
        setTimeout(() => setStatusUpdateSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Hiba a zártkörű esemény törlése során:', error);
      setStatusUpdateError('Hiba történt a zártkörű esemény törlése során.');
      setTimeout(() => setStatusUpdateError(null), 3000);
    } finally {
      setDeletingPrivateEvent(null);
    }
  };

  if (!currentUser) {
    return null;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Foglalások</h1>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600">Nincs jogosultságod az oldal megtekintéséhez.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Vissza a vezérlőpultra
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navigation showPublicBooking={true} />

      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Foglalások kezelése</h1>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('list')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'list'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Lista nézet
              </button>
              <button
                onClick={() => setActiveTab('daily')}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'daily'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Napi áttekintés
              </button>
            </nav>
          </div>

          {/* Consolidated Daily View */}
          {activeTab === 'daily' && (
            <div className="mb-6 bg-white rounded-lg shadow">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Napi áttekintés - Összes esemény</h2>
                  <button
                    onClick={handlePrintDailyView}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    Nyomtatás
                  </button>
                </div>
                
                {/* Napi áttekintés szűrők */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <div className="flex-1 w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dátum kiválasztása</label>
                    <input
                      type="date"
                      value={consolidatedDate}
                      onChange={(e) => setConsolidatedDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1 w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Típus szűrő</label>
                    <select
                      value={filterType}
                      onChange={handleFilterTypeChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="all">Összes típus</option>
                      <option value="gokart">Gokart</option>
                      <option value="simulator">Szimulátor</option>
                      <option value="vr">VR</option>
                      <option value="playhouse">Játszóház</option>
                    </select>
                  </div>
                  <div className="flex-1 w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Állapot szűrő</label>
                    <select
                      value={filterStatus}
                      onChange={handleFilterStatusChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="all">Összes állapot</option>
                      <option value="pending">Függőben</option>
                      <option value="confirmed">Megerősítve</option>
                      <option value="completed">Teljesítve</option>
                      <option value="cancelled">Lemondva</option>
                      <option value="private">Csak zártkörű események</option>
                    </select>
                  </div>
                  {(filterType !== 'all' || filterStatus !== 'all') && (
                    <div className="flex items-center">
                      <button
                        onClick={() => {
                          setFilterType('all');
                          setFilterStatus('all');
                        }}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        Szűrők törlése
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{consolidatedEvents.filter(event => {
                      // Apply filters to the displayed events count
                      if (filterType !== 'all' && event.serviceType !== filterType) return false;
                      if (filterStatus === 'private' && event.type !== 'private') return false;
                      if (filterStatus !== 'all' && filterStatus !== 'private' && event.type === 'booking' && event.status !== filterStatus) return false;
                      return true;
                    }).length}</span> esemény összesen
                  </div>
                </div>

                {/* Loading indikátor, ha még nincsenek betöltve az adatok */}
                {bookings.length === 0 && groupedBookings.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-gray-600">Foglalások betöltése...</span>
                    </div>
                  </div>
                ) : consolidatedEvents.filter(event => {
                  // Apply filters to displayed events
                  if (filterType !== 'all' && event.serviceType !== filterType) return false;
                  if (filterStatus === 'private' && event.type !== 'private') return false;
                  if (filterStatus !== 'all' && filterStatus !== 'private' && event.type === 'booking' && event.status !== filterStatus) return false;
                  return true;
                }).length > 0 ? (
                  <div className="space-y-2">
                    {consolidatedEvents.filter(event => {
                      // Apply filters to displayed events
                      if (filterType !== 'all' && event.serviceType !== filterType) return false;
                      if (filterStatus === 'private' && event.type !== 'private') return false;
                      if (filterStatus !== 'all' && filterStatus !== 'private' && event.type === 'booking' && event.status !== filterStatus) return false;
                      return true;
                    }).map((event) => (
                      <div
                        key={event.id}
                                                 className={`p-4 rounded-lg border-l-4 ${
                           event.type === 'private' 
                             ? 'bg-yellow-50 border-yellow-400' 
                             : event.type === 'booking' && event.status === 'confirmed' 
                               ? 'bg-green-50 border-green-400'
                               : event.type === 'booking' && event.status === 'pending'
                                 ? 'bg-blue-50 border-blue-400'
                                 : event.type === 'booking' && event.status === 'completed'
                                   ? 'bg-gray-50 border-gray-400'
                                   : event.type === 'booking' && event.status === 'cancelled'
                                     ? 'bg-red-50 border-red-400'
                                     : 'bg-gray-50 border-gray-400'
                         }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {event.startTime} - {event.endTime}
                              </span>
                                                             <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                 event.type === 'private'
                                   ? 'bg-yellow-100 text-yellow-800'
                                   : event.serviceType === 'gokart'
                                     ? 'bg-green-100 text-green-800'
                                     : event.serviceType === 'vr'
                                       ? 'bg-purple-100 text-purple-800'
                                       : event.serviceType === 'simulator'
                                         ? 'bg-blue-100 text-blue-800'
                                         : 'bg-orange-100 text-orange-800'
                               }`}>
                                 {event.type === 'private' ? 'Zártkörű' : getServiceTypeLabel(event.serviceType)}
                               </span>
                               {event.type === 'booking' && event.status && (
                                 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                   event.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                   event.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                                   event.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                                   'bg-red-100 text-red-800'
                                 }`}>
                                   {statusTranslations[event.status]}
                                 </span>
                               )}
                            </div>
                            <h3 className="font-medium text-gray-900">{event.title}</h3>
                            <p className="text-sm text-gray-600">{event.description}</p>
                            {event.notes && (
                              <p className="text-sm text-gray-500 mt-1">
                                <span className="font-medium">Megjegyzés:</span> {event.notes}
                              </p>
                            )}
                            {event.type === 'booking' && event.groupInfo && (
                              <div className="mt-2">
                                <button
                                  onClick={() => viewGroupDetails(event.groupInfo!)}
                                  className="text-indigo-600 hover:text-indigo-900 text-sm flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                  </svg>
                                  Részletek ({event.groupInfo.bookings.length} foglalás)
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {event.type === 'private' ? (
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center space-x-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-xs text-gray-500">Zártkörű esemény</span>
                                </div>
                                <button
                                  onClick={() => {
                                    let eventDate: string;
                                    try {
                                      if (event.date instanceof Date) {
                                        eventDate = event.date.toISOString().split('T')[0];
                                      } else if (event.date && typeof event.date.toDate === 'function') {
                                        eventDate = event.date.toDate().toISOString().split('T')[0];
                                      } else {
                                        console.warn('Ismeretlen dátum formátum a törlés gombban:', event.date);
                                        return;
                                      }
                                      handleDeletePrivateEvent(event.id, eventDate, event.startTime, event.endTime);
                                    } catch (error) {
                                      console.error('Hiba a dátum feldolgozása során a törlés gombban:', error, event.date);
                                    }
                                  }}
                                  disabled={deletingPrivateEvent === event.id}
                                  className="text-red-600 hover:text-red-900 p-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Zártkörű esemény törlése"
                                >
                                  {deletingPrivateEvent === event.id ? (
                                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                {event.groupInfo && event.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => updateGroupStatus(event.groupInfo!, 'confirmed')}
                                      className="text-green-600 hover:text-green-900 p-1 rounded"
                                      title="Csoport elfogadása"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => updateGroupStatus(event.groupInfo!, 'cancelled')}
                                      className="text-red-600 hover:text-red-900 p-1 rounded"
                                      title="Csoport elutasítása"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </>
                                )}
                                {event.groupInfo && event.status === 'confirmed' && (
                                  <button
                                    onClick={() => updateGroupStatus(event.groupInfo!, 'completed')}
                                    className="text-blue-600 hover:text-blue-900 p-1 rounded"
                                    title="Csoport teljesítve"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                                {event.groupInfo && (
                                  <button
                                    onClick={() => handleDeleteGroup(event.groupInfo!)}
                                    className="text-red-600 hover:text-red-900 p-1 rounded"
                                    title="Csoport törlése"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0v1h12V7M5 10h14v11a2 2 0 01-2 2H7a2 2 0 01-2-2V10z" />
                    </svg>
                    <p>
                      {consolidatedEvents.length === 0 
                        ? 'Nincsenek események a kiválasztott napon' 
                        : 'Nincsenek események a kiválasztott szűrőkkel'
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* List View */}
          {activeTab === 'list' && (
            <>
              {/* Dátum szűrő */}
              <div className="mt-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end">
            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Szűrés dátuma szerint</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filterDate || ''}
                  onChange={handleFilterDateChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Év szerint</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              >
                <option value="all">Összes év</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Hónap szerint</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                onChange={(e) => setFilterMonth(e.target.value)}
                value={filterMonth}
              >
                <option value="all">Összes hónap</option>
                {Array.from({ length: 12 }, (_, i) => {
                  const date = new Date();
                  date.setMonth(i);
                  return `${String(i + 1).padStart(2, '0')}`;
                }).map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, parseInt(month) - 1).toLocaleDateString('hu-HU', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Szűrés típus szerint</label>
              <select
                value={filterType}
                onChange={handleFilterTypeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="all">Összes típus</option>
                <option value="gokart">Gokart</option>
                <option value="simulator">Szimulátor</option>
                <option value="vr">VR</option>
                <option value="playhouse">Játszóház</option>
              </select>
            </div>
            <div className="flex-1 w-full sm:w-auto">
              <label className="block text-sm font-medium text-gray-700 mb-1">Szűrés állapot szerint</label>
              <select
                value={filterStatus}
                onChange={handleFilterStatusChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="all">Összes állapot</option>
                <option value="pending">Függőben</option>
                <option value="confirmed">Megerősítve</option>
                <option value="completed">Teljesítve</option>
                <option value="cancelled">Lemondva</option>
                <option value="mixed">Vegyes</option>
                <option value="private">Csak zártkörű események</option>
              </select>
            </div>
            {(filterDate || filterType !== 'all' || filterStatus !== 'all' || filterYear !== 'all' || filterMonth !== 'all') && (
              <div className="flex items-center">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Szűrők törlése
                </button>
              </div>
            )}
          </div>

          {statusUpdateSuccess && (
            <div className="mt-4 p-3 bg-green-100 text-green-800 rounded-md">
              A foglalás státusza sikeresen frissítve!
            </div>
          )}

          {statusUpdateError && (
            <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-md">
              {statusUpdateError}
            </div>
          )}

          {showAddForm && (
            <div className="mt-6 bg-white p-6 rounded-lg shadow">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Új foglalás hozzáadása</h2>
              
              <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">Meglévő vendég kiválasztása</h4>
                    <p className="text-xs text-blue-700">Tallózd a már regisztrált vendégek között és töltsd ki automatikusan az adatokat</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleToggleNewBookingUserBrowser}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    {showNewBookingUserBrowser ? 'Elrejtés' : 'Tallózás'}
                  </button>
                </div>

                {showNewBookingUserBrowser && (
                  <div className="mt-3">
                    <div className="mb-2">
                      <input
                        type="text"
                        placeholder="Keresés név, email vagy telefon alapján..."
                        value={newBookingRacerSearchTerm}
                        onChange={(e) => setNewBookingRacerSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                      {availableRacers
                        .filter(racer => 
                          !newBookingRacerSearchTerm || 
                          (racer.fullName || '').toLowerCase().includes(newBookingRacerSearchTerm.toLowerCase()) ||
                          (racer.email || '').toLowerCase().includes(newBookingRacerSearchTerm.toLowerCase()) ||
                          (racer.phoneNumber || '').includes(newBookingRacerSearchTerm)
                        )
                        .slice(0, 10)
                        .map((racer) => (
                          <div
                            key={racer.id}
                            onClick={() => handleSelectRacerForNewBooking(racer)}
                            className="p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {racer.fullName || 'Névtelen'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {racer.email && <span className="mr-2">{racer.email}</span>}
                                  {racer.phoneNumber && <span>{racer.phoneNumber}</span>}
                                </div>
                                {racer.racerId && (
                                  <div className="text-xs text-blue-600">ID: {racer.racerId}</div>
                                )}
                              </div>
                              <div className="text-xs text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                      {availableRacers.filter(racer => 
                        !newBookingRacerSearchTerm || 
                        (racer.fullName || '').toLowerCase().includes(newBookingRacerSearchTerm.toLowerCase()) ||
                        (racer.email || '').toLowerCase().includes(newBookingRacerSearchTerm.toLowerCase()) ||
                        (racer.phoneNumber || '').includes(newBookingRacerSearchTerm)
                      ).length === 0 && (
                        <div className="p-3 text-center text-gray-500 text-sm">
                          Nincs megfelelő vendég
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <form onSubmit={handleAddBooking}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ügyfél neve</label>
                    <input
                      type="text"
                      name="userName"
                      value={newBooking.userName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ügyfél email</label>
                    <input
                      type="email"
                      name="userEmail"
                      value={newBooking.userEmail}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gokart száma</label>
                    <select
                      name="kartNumber"
                      value={newBooking.kartNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    >
                      {availableKarts.map(kart => (
                        <option key={kart} value={kart}>Gokart {kart}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dátum</label>
                    <input
                      type="date"
                      name="date"
                      value={newBooking.date}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Kezdés időpontja</label>
                    <input
                      type="time"
                      name="startTime"
                      value={newBooking.startTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Befejezés időpontja</label>
                    <input
                      type="time"
                      name="endTime"
                      value={newBooking.endTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Foglalás hozzáadása
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="mt-6 bg-white overflow-hidden shadow-sm rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">Foglalások betöltése...</span>
                </div>
              </div>
            ) : groupedBookings.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-600">Nincsenek foglalások.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                          onClick={handleSortByDate}
                        >
                          <div className="flex items-center">
                            Dátum
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 ml-1 ${sortOrder === 'asc' ? 'transform rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Időpont</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Típus</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendégek</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedBookings.map((group, groupIndex) => (
                        <tr key={`group-${groupIndex}`}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{formatDate(group.date)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{group.startTime} - {group.endTime}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {group.hasPrivateEvents && group.bookings.length === 0 ? (
                              group.privateEvents?.map((privateEvent, index) => (
                                <span key={`private-type-${index}`} className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1">
                                  Zártkörű {getServiceTypeLabel(privateEvent.serviceType)}
                                </span>
                              ))
                            ) : (
                              <>
                                {group.bookings.some(b => b.bookingType === 'gokart') && (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 mr-1">
                                    Gokart
                                  </span>
                                )}
                                {group.bookings.some(b => b.bookingType === 'simulator') && (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 mr-1">
                                    Szimulátor
                                  </span>
                                )}
                                {group.bookings.some(b => b.bookingType === 'vr') && (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 mr-1">
                                    VR
                                  </span>
                                )}
                                {group.bookings.some(b => b.bookingType === 'playhouse') && (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 mr-1">
                                    Játszóház
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {group.hasPrivateEvents && group.bookings.length === 0 ? (
                              <div className="space-y-2">
                                {group.privateEvents?.map((privateEvent, index) => (
                                  <div key={`private-${index}`} className="flex items-start space-x-2 pb-2 border-b border-gray-100 last:border-0">
                                    <div className="flex-shrink-0 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                                      Zártkörű {getServiceTypeLabel(privateEvent.serviceType)}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">
                                        {privateEvent.description || 'Zártkörű esemény'}
                                      </div>
                                      {privateEvent.notes && (
                                        <div className="text-xs text-gray-500">{privateEvent.notes}</div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {group.bookings.map((booking) => (
                                  <div key={booking.id} className="flex items-start space-x-2 pb-2 border-b border-gray-100 last:border-0">
                                    <div className={`flex-shrink-0 ${booking.bookingType === 'gokart' ? 'bg-green-100 text-green-800' :
                                      booking.bookingType === 'simulator' ? 'bg-blue-100 text-blue-800' :
                                        booking.bookingType === 'vr' ? 'bg-purple-100 text-purple-800' :
                                          'bg-yellow-100 text-yellow-800'
                                      } px-2 py-1 rounded-full text-xs font-medium`}>
                                      {booking.bookingType === 'gokart'
                                        ? `Gokart ${booking.itemNumber}`
                                        : booking.bookingType === 'simulator'
                                          ? `Szimulátor ${booking.itemNumber}`
                                          : booking.bookingType === 'vr'
                                            ? `VR ${booking.itemNumber}`
                                            : `Játszóház ${booking.itemNumber}`}
                                    </div>
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-gray-900">{booking.userName}</div>
                                      <div className="text-xs text-gray-500">{booking.userEmail}</div>
                                      {booking.racerId && (
                                        <div className="text-xs text-gray-500">ID: {booking.racerId}</div>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end space-y-1">
                                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                        ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                          booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                            booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                              'bg-yellow-100 text-yellow-800'}`}>
                                        {statusTranslations[booking.status]}
                                      </span>
                                      <div className="flex space-x-1">
                                        <button
                                          onClick={() => handleEditBooking(booking)}
                                          className="text-blue-600 hover:text-blue-900 text-xs flex items-center"
                                          title="Foglalás szerkesztése"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                          </svg>
                                          Szerk.
                                        </button>
                                        <button
                                          onClick={() => handleDeleteBooking(booking.id, booking.bookingType, booking.userName, booking.itemNumber, booking.date, booking.startTime)}
                                          className="text-red-600 hover:text-red-900 text-xs flex items-center"
                                          title="Foglalás törlése"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                          </svg>
                                          Törlés
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {/* Check if this is a private event group */}
                            {group.hasPrivateEvents && group.bookings.length === 0 ? (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                                Zártkörű esemény
                              </span>
                            ) : group.bookings.length > 0 ? (
                              /* Ellenőrizzük, hogy minden foglalásnak ugyanaz-e a státusza */
                              group.bookings.every(b => b.status === group.bookings[0].status) ? (
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${group.bookings[0].status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                    group.bookings[0].status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                      group.bookings[0].status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'}`}>
                                  {group.bookings[0].status === 'confirmed' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  {group.bookings[0].status === 'pending' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  {group.bookings[0].status === 'completed' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  {group.bookings[0].status === 'cancelled' && (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                  {statusTranslations[group.bookings[0].status]}
                                </span>
                              ) : (
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 inline" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                  </svg>
                                  Vegyes
                                </span>
                              )
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Nincs adat
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {group.hasPrivateEvents && group.bookings.length === 0 ? (
                              <div className="flex flex-col space-y-2">
                                {group.privateEvents?.map((privateEvent, index) => (
                                  <div key={`private-action-${index}`} className="flex flex-col space-y-1">
                                    <button
                                      onClick={() => viewGroupDetails(group)}
                                      className="text-indigo-600 hover:text-indigo-900 flex items-center text-xs"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                      </svg>
                                      Részletek
                                    </button>
                                    <button
                                      onClick={() => {
                                        const eventId = `${privateEvent.date}-${privateEvent.startHour}-${privateEvent.endHour}-${privateEvent.serviceType}`;
                                        handleDeletePrivateEvent(eventId, privateEvent.date, `${privateEvent.startHour}:00`, `${privateEvent.endHour}:00`);
                                      }}
                                      className="text-red-600 hover:text-red-900 flex items-center text-xs"
                                      title="Zártkörű esemény törlése"
                                      disabled={!!deletingPrivateEvent}
                                    >
                                      {deletingPrivateEvent ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
                                      ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                      Törlés
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex flex-col space-y-2">
                                {/* Csoportos műveletek */}
                                <>
                                  {/* Összes megerősítése gomb - megjelenik, ha van legalább egy függőben lévő foglalás */}
                                  {group.bookings.some(b => b.status === 'pending') && (
                                    <button
                                      onClick={() => updateGroupStatus(group, 'confirmed')}
                                      className="text-green-600 hover:text-green-900 flex items-center"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                      Összes elfogadása
                                    </button>
                                  )}
                                  {/* Összes elutasítása gomb - csak akkor jelenik meg, ha minden foglalás függőben van */}
                                  {group.bookings.every(b => b.status === 'pending') && (
                                    <button
                                      onClick={() => updateGroupStatus(group, 'cancelled')}
                                      className="text-red-600 hover:text-red-900 flex items-center"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                      </svg>
                                      Összes elutasítása
                                    </button>
                                  )}
                                  {/* Összes teljesítve gomb - csak akkor jelenik meg, ha van legalább egy megerősített foglalás */}
                                  {group.bookings.some(b => b.status === 'confirmed') && (
                                    <button
                                      onClick={() => updateGroupStatus(group, 'completed')}
                                      className="text-blue-600 hover:text-blue-900 flex items-center"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                      Összes teljesítve
                                    </button>
                                  )}
                                </>
                                {/* Csoportos törlés gomb */}
                                <button
                                  onClick={() => handleDeleteGroup(group)}
                                  className="text-red-600 hover:text-red-900 flex items-center"
                                  title="Összes foglalás törlése"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  Összes törlése
                                </button>
                                {/* Részletek gomb */}
                                <button
                                  onClick={() => viewGroupDetails(group)}
                                  className="text-indigo-600 hover:text-indigo-900 flex items-center"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                  </svg>
                                  Részletek
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Lapozó */}
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  setItemsPerPage={setItemsPerPage}
                  list={groupedBookings}
                />
              </>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Foglalás csoport részletek modal */}
      {showGroupDetails && selectedGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-7xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Foglalás részletei</h3>
              <button
                onClick={closeGroupDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 bg-gray-50 p-3 rounded-md">
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                <div>
                  <p className="text-sm text-gray-500">Dátum:</p>
                  <p className="text-sm font-medium">{formatDate(selectedGroup.date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Időpont:</p>
                  <p className="text-sm font-medium">{selectedGroup.startTime} - {selectedGroup.endTime}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Foglalások száma:</p>
                  <p className="text-sm font-medium">
                    {selectedGroup.bookings.length} {
                      selectedGroup.bookings.every(b => b.bookingType === 'gokart') ? 'gokart' :
                        selectedGroup.bookings.every(b => b.bookingType === 'simulator') ? 'szimulátor' :
                          selectedGroup.bookings.every(b => b.bookingType === 'vr') ? 'VR' :
                            selectedGroup.bookings.every(b => b.bookingType === 'playhouse') ? 'játszóház' :
                              'különböző foglalás'
                    }
                  </p>
                </div>
                <div className="col-span-3 flex justify-end items-center">
                  <button
                    onClick={() => {
                      closeGroupDetails();
                      handleDeleteGroup(selectedGroup);
                    }}
                    className="text-red-600 hover:text-red-900 flex items-center text-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Összes foglalás törlése
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <h4 className="text-md font-medium text-gray-800 mb-2">Vendégek</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Foglalás típusa</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendég</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Állapot</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Műveletek</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedGroup.bookings.map((booking) => (
                      <tr key={booking.id}>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className={`text-sm font-medium flex items-center ${booking.bookingType === 'gokart' ? 'text-green-600' :
                            booking.bookingType === 'simulator' ? 'text-blue-600' :
                              booking.bookingType === 'vr' ? 'text-purple-600' :
                                'text-yellow-600'
                            }`}>
                            {booking.bookingType === 'gokart' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2a2 2 0 012 2v3a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-9a1 1 0 00-.293-.707l-2-2A1 1 0 0017 3h-3a1 1 0 00-1 1v4H3z" />
                              </svg>
                            )}
                            {booking.bookingType === 'simulator' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            )}
                            {booking.bookingType === 'vr' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            )}
                            {booking.bookingType === 'playhouse' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                              </svg>
                            )}
                            <div>
                              {booking.bookingType === 'gokart' && `Gokart #${booking.itemNumber}`}
                              {booking.bookingType === 'simulator' && `Szimulátor #${booking.itemNumber}`}
                              {booking.bookingType === 'vr' && `VR #${booking.itemNumber}`}
                              {booking.bookingType === 'playhouse' && `Játszóház #${booking.itemNumber}`}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-sm font-medium flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            {booking.userName}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-sm text-gray-500 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            {booking.userEmail}
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'}`}>
                            {booking.status === 'confirmed' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                            {booking.status === 'pending' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                              </svg>
                            )}
                            {booking.status === 'completed' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                            {booking.status === 'cancelled' && (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            )}
                            {statusTranslations[booking.status]}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditBooking(booking)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Szerkesztés"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            </button>
                            {booking.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => updateBookingStatus(booking.id, booking.bookingType, 'confirmed')}
                                  className="text-green-600 hover:text-green-900"
                                  title="Elfogadás"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => updateBookingStatus(booking.id, booking.bookingType, 'cancelled')}
                                  className="text-red-600 hover:text-red-900"
                                  title="Elutasítás"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </>
                            )}
                            {/* Csak akkor mutatjuk a "Teljesítve" gombot, ha a foglalás megerősített és nem lemondott */}
                            {booking.status === 'confirmed' && (
                              <button
                                onClick={() => updateBookingStatus(booking.id, booking.bookingType, 'completed')}
                                className="text-blue-600 hover:text-blue-900"
                                title="Teljesítve"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteBooking(booking.id, booking.bookingType, booking.userName, booking.itemNumber, booking.date, booking.startTime)}
                              className="text-red-600 hover:text-red-900"
                              title="Törlés"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Vendégek részletes adatai - Kompaktabb elrendezés */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-md font-medium text-gray-800">Vendégek részletes adatai</h4>
                <button
                  onClick={() => handleAddGuestToGroup(selectedGroup)}
                  className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 flex items-center"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Plusz vendég
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                {selectedGroup.bookings.map((booking) => (
                  <div key={`details-${booking.id}`} className="bg-gray-50 p-2 rounded-md">
                    <div className="flex items-center mb-1 bg-white p-1 rounded-md shadow-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 mr-1 ${booking.bookingType === 'gokart' ? 'text-green-600' :
                        booking.bookingType === 'simulator' ? 'text-blue-600' :
                          booking.bookingType === 'vr' ? 'text-purple-600' :
                            'text-yellow-600'
                        }`} viewBox="0 0 20 20" fill="currentColor">
                        {booking.bookingType === 'gokart' ? (
                          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                        ) : booking.bookingType === 'simulator' ? (
                          <path d="M13 7H7v6h6V7z" />
                        ) : booking.bookingType === 'vr' ? (
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                        ) : (
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        )}
                      </svg>
                      <h5 className="text-sm font-medium text-gray-800">
                        {booking.userName} (
                        {booking.bookingType === 'gokart' && `Gokart #${booking.itemNumber}`}
                        {booking.bookingType === 'simulator' && `Szimulátor #${booking.itemNumber}`}
                        {booking.bookingType === 'vr' && `VR #${booking.itemNumber}`}
                        {booking.bookingType === 'playhouse' && `Játszóház #${booking.itemNumber}`}
                        )
                      </h5>
                    </div>

                    <div className="grid grid-cols-1 gap-1 text-xs">
                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-500 mr-1">Anyja:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.motherName || booking.motherName || 'Nincs megadva'}
                        </span>
                      </div>

                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 012-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-500 mr-1">Születési hely:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.birthPlace || booking.birthPlace || 'Nincs megadva'}
                        </span>
                      </div>

                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 012-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-500 mr-1">Születési idő:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.birthDate || booking.birthDate || 'Nincs megadva'}
                        </span>
                      </div>

                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-500 mr-1">Cím:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.address || booking.address || 'Nincs megadva'}
                        </span>
                      </div>

                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                        <span className="text-gray-500 mr-1">Email:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.email || booking.userEmail || 'Nincs megadva'}
                        </span>
                      </div>

                      <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        <span className="text-gray-500 mr-1">Telefon:</span>
                        <span className="font-medium truncate">
                          {booking.racerDetails?.phoneNumber || booking.phoneNumber || 'Nincs megadva'}
                        </span>
                      </div>

                      {booking.racerId && (
                        <div className="bg-white p-1 rounded-md shadow-sm flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-500 mr-1">Vendég ID:</span>
                          <span className="font-medium truncate">
                            {booking.racerId}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 flex justify-end">
              <button
                onClick={closeGroupDetails}
                className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Szerkesztő modal */}
      {showEditModal && editingBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Foglalás szerkesztése - {editingBooking.bookingType === 'gokart' ? `Gokart #${editingBooking.itemNumber}` :
                  editingBooking.bookingType === 'simulator' ? `Szimulátor #${editingBooking.itemNumber}` :
                    editingBooking.bookingType === 'vr' ? `VR #${editingBooking.itemNumber}` :
                      `Játszóház #${editingBooking.itemNumber}`}
              </h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Dátum:</span>
                  <span className="ml-2">{formatDate(editingBooking.date)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Időpont:</span>
                  <span className="ml-2">{editingBooking.startTime} - {editingBooking.endTime}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Állapot:</span>
                  <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${
                    editingBooking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    editingBooking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    editingBooking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {statusTranslations[editingBooking.status]}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Racer ID:</span>
                  <span className="ml-2">{editingBooking.racerId || 'Nincs'}</span>
                </div>
              </div>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Vendég hozzárendelése</h4>
                  <p className="text-xs text-blue-700">Tallózd a már regisztrált vendégek között vagy adj hozzá újat</p>
                  {editingBooking?.racerDetails && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-xs text-green-800">
                          Hozzárendelve: <strong>{editingBooking.racerDetails.fullName}</strong> (ID: {editingBooking.racerDetails.racerId})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-2">
                  {editingBooking?.racerDetails && (
                    <button
                      type="button"
                      onClick={() => {
                        if (editingBooking) {
                          setEditingBooking({
                            ...editingBooking,
                            racerId: undefined,
                            racerDetails: undefined
                          });
                        }
                      }}
                      className="px-3 py-1 bg-red-600 text-white rounded-md text-sm hover:bg-red-700 flex items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Eltávolítás
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleToggleUserBrowser}
                    className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                    {showUserBrowser ? 'Elrejtés' : 'Tallózás'}
                  </button>
                </div>
              </div>

                              {showUserBrowser && (
                  <div className="mt-3">
                    <div className="mb-3 flex justify-between items-center">
                      <input
                        type="text"
                        placeholder="Keresés név, email vagy telefon alapján..."
                        value={racerSearchTerm}
                        onChange={(e) => setRacerSearchTerm(e.target.value)}
                        className="flex-1 mr-2 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewRacerForm(true)}
                        className="px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Új vendég
                      </button>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                      {availableRacers
                        .filter(racer => 
                          !racerSearchTerm || 
                          (racer.fullName || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                          (racer.email || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                          (racer.phoneNumber || '').includes(racerSearchTerm)
                        )
                        .slice(0, 10)
                        .map((racer) => (
                          <div
                            key={racer.id}
                            onClick={() => handleSelectRacer(racer)}
                            className="p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {racer.fullName || 'Névtelen'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {racer.email && <span className="mr-2">{racer.email}</span>}
                                  {racer.phoneNumber && <span>{racer.phoneNumber}</span>}
                                </div>
                                {racer.racerId && (
                                  <div className="text-xs text-blue-600">ID: {racer.racerId}</div>
                                )}
                              </div>
                              <div className="text-xs text-gray-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                      {availableRacers.filter(racer => 
                        !racerSearchTerm || 
                        (racer.fullName || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                        (racer.email || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                        (racer.phoneNumber || '').includes(racerSearchTerm)
                      ).length === 0 && (
                        <div className="p-3 text-center text-gray-500 text-sm">
                          Nincs megfelelő vendég
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Név <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="userName"
                    value={editFormData.userName}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="userEmail"
                    value={editFormData.userEmail}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anyja neve
                  </label>
                  <input
                    type="text"
                    name="motherName"
                    value={editFormData.motherName}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Születési dátum
                  </label>
                  <input
                    type="date"
                    name="birthDate"
                    value={editFormData.birthDate}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cím
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefonszám
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={editFormData.phoneNumber}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Személyi igazolvány szám
                  </label>
                  <input
                    type="text"
                    name="idNumber"
                    value={editFormData.idNumber}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Mentés
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Új vendég hozzáadása modal */}
      {showNewRacerForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Új vendég hozzáadása</h2>
              <button
                onClick={handleCancelNewRacer}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleSaveNewRacer(); }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Név <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={newRacerData.fullName}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={newRacerData.email}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Anyja neve</label>
                  <input
                    type="text"
                    name="motherName"
                    value={newRacerData.motherName}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Születési dátum</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={newRacerData.birthDate}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cím</label>
                  <input
                    type="text"
                    name="address"
                    value={newRacerData.address}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefonszám</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={newRacerData.phoneNumber}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Személyi igazolvány szám</label>
                  <input
                    type="text"
                    name="idCardNumber"
                    value={newRacerData.idCardNumber}
                    onChange={handleNewRacerFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancelNewRacer}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={savingNewRacer}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {savingNewRacer && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Vendég hozzáadása
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vendég hozzáadása modal */}
      {showAddGuestModal && addingGuestToGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                Vendég hozzáadása - {formatDate(addingGuestToGroup.date)} {addingGuestToGroup.startTime}
              </h2>
              <button
                onClick={handleCancelAddGuest}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 rounded-md border border-blue-200">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Válassz vendéget a meglévők közül</h4>
              <input
                type="text"
                placeholder="Keresés név, email vagy telefon alapján..."
                value={racerSearchTerm}
                onChange={(e) => setRacerSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
              />
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                {availableRacers
                  .filter(racer => 
                    !racerSearchTerm || 
                    (racer.fullName || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                    (racer.email || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                    (racer.phoneNumber || '').includes(racerSearchTerm)
                  )
                  .slice(0, 15)
                  .map((racer) => (
                    <div
                      key={racer.id}
                      onClick={() => !addingGuest && handleSelectRacerForGroup(racer)}
                      className={`p-3 border-b border-gray-100 last:border-0 ${
                        addingGuest 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {racer.fullName || 'Névtelen'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {racer.email && <span className="mr-3">📧 {racer.email}</span>}
                            {racer.phoneNumber && <span className="mr-3">📱 {racer.phoneNumber}</span>}
                          </div>
                          {racer.racerId && (
                            <div className="text-xs text-blue-600 mt-1">🆔 {racer.racerId}</div>
                          )}
                          {racer.motherName && (
                            <div className="text-xs text-gray-500 mt-1">👩 Anyja: {racer.motherName}</div>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))
                }
                {availableRacers.filter(racer => 
                  !racerSearchTerm || 
                  (racer.fullName || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                  (racer.email || '').toLowerCase().includes(racerSearchTerm.toLowerCase()) ||
                  (racer.phoneNumber || '').includes(racerSearchTerm)
                ).length === 0 && (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    Nincs megfelelő vendég
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleCancelAddGuest}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Mégse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings; 